import { useEffect, useMemo, useRef } from "react"
import { useCurrentState } from "lib/@hooks/useCurrentState"
import { CancelAsync, Terminated, TerminatedError } from "lib/@hooks/useAsync"
import { scheduleIdle as defaultScheduleIdle, cancelIdle as defaultCancelIdle } from "lib/schedule-idle-callback"

export { CancelAsync, Terminated, TerminatedError }

const defaultIdleScheduler = {
    scheduleIdle: defaultScheduleIdle,
    cancelIdle: defaultCancelIdle,
}

/**
 * Like useAsync, but the producer receives yieldIdle instead of update.
 * await yieldIdle() schedules the next idle slice; await yieldIdle(value) commits a progressive update first.
 *
 * @param {Function} promiseProducer
 * @param {any} [defaultValue]
 * @param {string|object} [refId]
 * @param {Function} [check]
 * @param {boolean} [logKey]
 * @param {{ scheduleIdle: Function, cancelIdle: Function }} [idleScheduler]
 */
export function useIdleAsync(
    promiseProducer,
    defaultValue = null,
    refId = "standard",
    check = () => false,
    logKey = false,
    idleScheduler = defaultIdleScheduler
) {
    promiseProducer = typeof promiseProducer !== "function" ? async () => promiseProducer : promiseProducer

    const { scheduleIdle, cancelIdle } = idleScheduler ?? defaultIdleScheduler
    const executionId = useRef()
    const pendingIdleRef = useRef(null)
    const key = typeof refId === "object" ? JSON.stringify(refId) : refId
    const value = useRef(defaultValue)
    const [, setResult] = useCurrentState(0)

    useEffect(
        () => () => {
            executionId.current = Terminated
            cancelPendingIdle()
        },
        []
    )

    useMemo(startRun, [key])

    return value.current

    function startRun() {
        cancelPendingIdle()
        executionId.current = key
        if (logKey) console.log("Running idle async", key)
        run()
    }

    function run() {
        runMe().catch(console.error)

        async function runMe() {
            if (key !== executionId.current) return

            try {
                const result = await promiseProducer(yieldIdle, key, value.current)
                if (key !== executionId.current) return
                if (result === CancelAsync) return

                if (
                    !check(value.current, result) &&
                    !!result &&
                    Object.isEqual(value.current, result || defaultValue || result)
                ) {
                    return
                }

                value.current = result === null ? null : result ?? defaultValue ?? result
                setResult((prev) => prev + 1)
            } catch (e) {
                if (!(e instanceof TerminatedError)) {
                    console.error(e)
                    value.current = e
                    setResult((prev) => prev + 1)
                }
            }
        }
    }

    function assertNotStale() {
        if (executionId.current === Terminated || key !== executionId.current) {
            throw new TerminatedError()
        }
    }

    function commitUpdate(update) {
        assertNotStale()
        value.current = update
        setResult((prev) => prev + 1)
    }

    function cancelPendingIdle() {
        if (pendingIdleRef.current != null) {
            cancelIdle(pendingIdleRef.current)
            pendingIdleRef.current = null
        }
    }

    function yieldIdle(update) {
        if (update !== undefined) {
            commitUpdate(update)
        }

        assertNotStale()
        cancelPendingIdle()

        return new Promise((resolve, reject) => {
            pendingIdleRef.current = scheduleIdle((deadline) => {
                pendingIdleRef.current = null
                if (executionId.current === Terminated || key !== executionId.current) {
                    reject(new TerminatedError())
                    return
                }
                resolve(toIdleSlice(deadline))
            })
        })
    }
}

function toIdleSlice(deadline) {
    if (deadline && typeof deadline.didTimeout === "boolean") {
        return {
            didTimeout: deadline.didTimeout,
            timeRemaining: () => (typeof deadline.timeRemaining === "function" ? deadline.timeRemaining() : 0),
        }
    }

    return {
        didTimeout: true,
        timeRemaining: () => 0,
    }
}

export default useIdleAsync
